import React, {useContext, useEffect, useMemo, useState} from "react";
import {AiAttributeServiceContext, AppContextContext} from "../../Contexts";
import {Breadcrumb, Button, message, Modal, Space, Table, Tag} from "antd";
import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Link, useLocation, useNavigate} from "react-router-dom";
import _ from "lodash";
import * as globalStyles from "../App.module.css";
import AiAttribute, {formatAiAttributeResultType} from "../../domain/AiAttribute";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import {ColumnsType} from "antd/es/table";
import MoreLess from "../common/MoreLess";
import * as styles from "./AiAttributeList.module.css";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import {Permission} from "../../domain/auth/Permission";
import {UserRole} from "../../domain/UserRole";
import {renderOrganizationLink} from "../common/OrganizationLink";
import Tip from "../common/Tip";
import {FormPersister} from "../../sal-ui/FormPersister";
import OwnerFilter, {createDefaultOwnerFilterValues, createOwnerRqbQuery, loadPersistentOwnerFilterValues} from "../common/OwnerFilter";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const PERSISTENT_IDENT = "AiAttributeList";
const FILTER_DEBOUNCE_MS = 500;

function AiAttributeList() {
    const appContext = useContext(AppContextContext);
    const aiAttributeService = useContext(AiAttributeServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [aiAttributes, setAiAttributes] = useState<PagedResult<AiAttribute>>();
    const [filterValues, setFilterValues] = useState<any>();
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);

    const title = "AI attributes";
    const hasManage = appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_ATTRIBUTE__MANAGE_ORG);
    const ownerFilterAccess = {
        canReadSystem: appContext.user.hasPermission(Permission.AI_ATTRIBUTE__READ_SEAL),
        canReadOrganization: appContext.user.hasPermission(Permission.AI_ATTRIBUTE__READ_ORG)
    };

    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: PERSISTENT_IDENT
    });

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const values = loadPersistentOwnerFilterValues({
            appContext,
            access: ownerFilterAccess,
            filterFormPersister,
            location,
            navigate
        });

        setFilterValues(values);
    }, []);

    useEffect(() => {
        if (filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <Tip
                    id="AiAttributeList.purpose"
                    style={{marginBottom: 12}}
                    message="AI attributes define values extracted from items, such as ratings, tags, or generated text."
                    description={
                        <>
                            SEAL uses them in{" "}
                            <Link to="/definitions/ai-extractors">AI extractors</Link> and{" "}
                            <Link to="/vul-source-items/views">views</Link>. Some attributes are managed automatically from{" "}
                            <Link to="/interests">interests</Link>.
                        </>
                    }
                />

                <OwnerFilter
                    className={styles.filter}
                    values={filterValues}
                    defaultValues={createDefaultOwnerFilterValues(appContext, ownerFilterAccess)}
                    persistentStateIdent={PERSISTENT_IDENT}
                    canReadSystem={ownerFilterAccess.canReadSystem}
                    canReadOrganization={ownerFilterAccess.canReadOrganization}
                    onChange={setFilterValues}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    {hasManage &&
                        <Button type={"primary"}
                                icon={<PlusOutlined/>}
                                onClick={() => navigate("/definitions/ai-attributes/add")}>
                            Add
                        </Button>
                    }
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
                </div>

                <Table
                    className={styles.table}
                    dataSource={aiAttributes?.data}
                    loading={tableHandler.loading}
                    pagination={tableHandler.pagination}
                    showSorterTooltip={false}
                    rowKey="id"
                    onChange={tableHandler.onTableChange}
                    columns={getColumns()}
                />
            </>
        </DocumentTitle>
    );

    function getColumns(): ColumnsType<AiAttribute> {
        const cols: ColumnsType<AiAttribute> = [
            {
                dataIndex: "name",
                title: "Name",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                defaultSortOrder: "ascend",
                render: renderName
            },
            {
                dataIndex: ["organization", "name"],
                key: "organization.name",
                title: "Owner",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                align: "left",
                render: renderOrganization
            },
            {
                dataIndex: "resultType",
                title: "Result type",
                width: 160,
                render: (value) => formatAiAttributeResultType(value)
            },
            {
                dataIndex: "prompt",
                title: "Prompt",
                render: (value) => value
                    ? <MoreLess>
                        <div className={styles.prompt}>{value}</div>
                    </MoreLess>
                    : <i>no prompt</i>
            },
            {
                dataIndex: "aiExtractors",
                title: "AI extractors",
                render: (aiExtractors: any[]) => aiExtractors && aiExtractors.length > 0
                    ? aiExtractors.map(c => (
                        <div key={c.id}>
                            <Link to={`/definitions/ai-extractors/${c.id}`}>{c.name}</Link>
                        </div>
                    ))
                    : <i>none</i>
            }
        ];

        if (hasManage) {
            cols.push({
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            });
        }

        return cols;
    }

    function renderName(value: unknown, aiAttribute: AiAttribute) {
        return (
            <Space>
                <Link to={`/definitions/ai-attributes/${aiAttribute.id}`}>{value}</Link>
                {isManagedByInterest(aiAttribute) && <Tag color="blue" title="This attribute is managed automatically through Interests.">managed</Tag>}
            </Space>
        );
    }

    function renderOrganization(value: any, aiAttribute: AiAttribute) {
        if (aiAttribute.owner === ResourceOwnerType.SYSTEM) {
            return <i>System</i>;
        }

        return renderOrganizationLink(aiAttribute.organization, value);
    }

    function renderAction(_value: unknown, aiAttribute: AiAttribute) {
        if (!isEditableForPrincipal(aiAttribute)) {
            return null;
        }

        return (
            <Space>
                <Button
                    icon={<DeleteFilled/>}
                    className={"ant-btn-icon-only"}
                    title={"Delete"}
                    onClick={() => {
                        Modal.confirm({
                            content: <>Do you really want to delete AI attribute <b>{aiAttribute.name}</b>?</>,
                            okText: "Delete",
                            cancelText: "Cancel",
                            onOk: () => onDeleteConfirm(aiAttribute)
                        });
                    }}
                />
            </Space>
        );
    }

    function isEditableForPrincipal(aiAttribute: AiAttribute) {
        if (isManagedByInterest(aiAttribute)) {
            return false;
        }

        if (aiAttribute?.owner === ResourceOwnerType.SYSTEM && appContext.user.role !== UserRole.SYSTEM_ADMIN) {
            return false;
        }

        return true;
    }

    function isManagedByInterest(aiAttribute: AiAttribute) {
        return !!aiAttribute.interestId;
    }

    function onDeleteConfirm(aiAttribute: AiAttribute) {
        aiAttributeService.delete(aiAttribute)
            .then(() => {
                message.success("AI attribute deleted.");

                tableHandler.reload();
            });
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createOwnerRqbQuery(values);

        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function reload() {
        return aiAttributeService.getList(tableHandler.queryOptions)
            .then(result => {
                tableHandler.updateTotal(result.total);

                setAiAttributes(result);
            });
    }
}

export default AiAttributeList;
